import { formatCurrency, pluralize } from "@sellar-io/common";
import { Icon } from "@sellar-io/components";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import sellarLogoNew from "../../../../images/sellar-logo-new.png";
import {
  Badge,
  BadgeBlue,
  BadgeLime,
} from "../../../../components/badge/Badge";
import { SlideTransition } from "../../../../components/transition/SlideTransition";

type PaymentOptionProps = {
  selected: boolean;
  visible: boolean;
  children: ReactNode;
  onSelect: () => void;
  onDeselect: () => void;
};

type SellarCreditPaymentOptionProps = PaymentOptionProps & {
  dueDate: Date;
  canUserViewBalance: boolean;
  balance: number;
};

export const SellarCreditPaymentOption = ({
  selected,
  visible,
  children,
  onSelect,
  onDeselect,
  dueDate,
  canUserViewBalance,
  balance,
}: SellarCreditPaymentOptionProps) => (
  <PaymentOption
    selected={selected}
    visible={visible}
    onSelect={onSelect}
    onDeselect={onDeselect}
    trigger={
      <div className="flex w-full flex-col items-start gap-1">
        <div className="flex w-full items-center justify-center">
          <p className="flex-1 text-start font-semibold md:w-auto">
            <span>Use my</span>{" "}
            <span className="inline-flex gap-1 bg-linear-to-r from-[#B557F1] to-[#D213A6] bg-clip-text text-transparent">
              <img src={sellarLogoNew} className="relative top-0.5 h-4 w-4" />
              <span>Sellar Credit</span>
            </span>{" "}
            <span>account</span>
          </p>
          <Badge className="bg-purple-100">
            <span className="text-[#C637CB]">Sellar Credit</span>
          </Badge>
        </div>
        <p className="text-start text-zinc-600">
          Put your order on your Sellar Credit tab, due for payment on{" "}
          {dayjs(dueDate).format("Do MMMM")}.
        </p>
        {canUserViewBalance && (
          <p className="text-sm text-zinc-600">
            Balance available {formatCurrency(balance)}
          </p>
        )}
      </div>
    }
  >
    {children}
  </PaymentOption>
);

type SupplierCreditPaymentOptionProps = PaymentOptionProps & {
  supplierName: string;
  title?: ReactNode;
  description: ReactNode;
};

export const SupplierCreditPaymentOption = ({
  selected,
  visible,
  children,
  onSelect,
  onDeselect,
  supplierName,
  title,
  description,
}: SupplierCreditPaymentOptionProps) => (
  <PaymentOption
    selected={selected}
    visible={visible}
    onSelect={onSelect}
    onDeselect={onDeselect}
    trigger={
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex w-full justify-between">
          <span className="text-start font-semibold">
            {title ?? `Use my credit account with ${supplierName}`}
          </span>
          <BadgeLime>Supplier Credit</BadgeLime>
        </div>
        {description}
      </div>
    }
  >
    {children}
  </PaymentOption>
);

type PayAtCheckoutPaymentOptionProps = PaymentOptionProps & {
  supplierName: string;
  title?: ReactNode;
  description?: ReactNode;
};

export const PayAtCheckoutPaymentOption = ({
  selected,
  visible,
  children,
  onSelect,
  onDeselect,
  supplierName,
  title,
  description,
}: PayAtCheckoutPaymentOptionProps) => (
  <PaymentOption
    selected={selected}
    visible={visible}
    onSelect={onSelect}
    onDeselect={onDeselect}
    trigger={
      <div className="flex w-full flex-1 flex-col gap-2">
        <div className="flex w-full justify-between">
          <span className="text-start font-semibold">
            {title ?? "Pay now by card"}
          </span>
          <BadgeBlue>Pay at checkout</BadgeBlue>
        </div>
        <span className="text-start text-zinc-500">
          {description ??
            `Your payment to ${supplierName} will be processed instantly, with no need for any back and forth.`}
        </span>
      </div>
    }
  >
    {children}
  </PaymentOption>
);

type BasePaymentOptionProps = PaymentOptionProps & {
  trigger: ReactNode;
};

export const PaymentOption = ({
  selected,
  visible,
  trigger,
  children,
  onSelect,
  onDeselect,
}: BasePaymentOptionProps) => {
  if (!visible) return null;

  // Controlled by payment selection — animate height from the external `selected` flag.
  return (
    <div className="group flex flex-1 flex-col">
      <button
        type="button"
        onClick={selected ? onDeselect : onSelect}
        className={twMerge(
          "flex w-full flex-1 flex-col items-start rounded-lg border-zinc-800 bg-white p-4 hover:bg-gray-50",
          selected && "rounded-b-none border-x border-t hover:bg-white",
        )}
        aria-expanded={selected}
      >
        {trigger}
      </button>
      <SlideTransition show={selected} parentClass="duration-200 ease-out">
        <div
          className={twMerge(
            "relative flex w-full flex-1 flex-col bg-white",
            selected && "rounded-b-lg border-x border-b border-zinc-800",
          )}
        >
          {children}
        </div>
      </SlideTransition>
    </div>
  );
};

export const SellarCreditContent = ({
  total,
  dueDate,
  nbDaysOfCredit,
}: {
  total: string;
  dueDate: Date;
  nbDaysOfCredit: number;
}) => (
  <div className="flex flex-col">
    <div className="relative flex flex-col gap-y-6 px-8 py-4 before:absolute before:top-10 before:left-9 before:h-[30%] before:w-0.5 before:border-l-4 before:border-zinc-300">
      <div className="flex items-start gap-4 before:relative before:top-1.5 before:z-10 before:h-3 before:w-3 before:rounded-full before:bg-zinc-600">
        <div className="flex flex-col">
          <span className="font-semibold">{formatCurrency(0)}</span>
          <span className="text-sm">Today</span>
        </div>
      </div>
      <div className="flex items-start gap-4 before:relative before:top-1.5 before:h-3 before:w-3 before:rounded-full before:bg-zinc-300">
        <div className="flex flex-col">
          <span className="font-semibold">{total}</span>
          <span className="text-sm">{`${dayjs(dueDate).format("DD/MM/YYYY")} (${nbDaysOfCredit} days)`}</span>
        </div>
      </div>
    </div>
    <div className="flex flex-col justify-center gap-4 rounded-b-md border-t border-t-gray-300 bg-zinc-50 p-4 text-sm text-zinc-600 sm:flex-row sm:px-0">
      <div className="flex items-center gap-2">
        <div className="rounded-sm bg-purple-100 p-1">
          <Icon
            size={0.7}
            icon="CALENDAR_CLOCK_OUTLINE"
            className="text-[#C637CB]"
          />
        </div>
        <span>
          Pay in {nbDaysOfCredit} {pluralize("day", nbDaysOfCredit)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="rounded-sm bg-purple-100 p-1">
          <Icon size={0.7} icon="NOTE_OFF_OUTLINE" className="text-[#C637CB]" />
        </div>
        <span>Less admin</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="rounded-sm bg-purple-100 p-1">
          <Icon size={0.7} icon="TRUCK_OUTLINE" className="text-[#C637CB]" />
        </div>
        <span>Supplier gets paid on dispatch</span>
      </div>
    </div>
  </div>
);
